import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TriangleAlert, Ban, Copy, MoveRight, Info } from "lucide-react";
import { ApiError, errorMessage, post } from "@/lib/api";
import { qk, useTree, useWorkspaces } from "@/lib/queries";
import { useUi } from "@/lib/store";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface Related {
  type: string;
  id: string;
  label: string;
  action: "move" | "copy" | "blocker" | "warning" | "clear";
  reason: string;
}
interface Preview {
  sameWorkspace: boolean;
  source: { id: string; name: string; kind: string };
  destination: { id: string; name: string; kind: string };
  visibility: { gainAccess: { displayName: string }[]; loseAccess: { displayName: string }[]; requiresAcknowledgement: boolean; explanation: string };
  related: Related[];
  blockers: Related[];
}

const ACTION_LABEL: Record<Related["action"], string> = {
  move: "Moves with the note",
  copy: "Copied to destination",
  blocker: "Blocks the move",
  warning: "Check",
  clear: "Changed",
};

export function MoveDialog(props: { open: boolean; onOpenChange(o: boolean): void; noteId: string; noteTitle: string; currentWorkspaceId: string }) {
  const workspaces = useWorkspaces();
  const qc = useQueryClient();
  const [dest, setDest] = useState(props.currentWorkspaceId);
  const [folderId, setFolderId] = useState<string>("");
  const tree = useTree(dest);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [choice, setChoice] = useState<"move_related" | "cancel">("move_related");
  const [error, setError] = useState<string | null>(null);

  const folderOptions = useMemo(() => {
    const folders = tree.data?.folders ?? [];
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string | null, prefix: string) => {
      for (const f of folders.filter((x) => x.parentId === parent)) {
        out.push({ id: f.id, label: `${prefix}${f.name}` });
        walk(f.id, `${prefix}${f.name} / `);
      }
    };
    walk(null, "");
    return out;
  }, [tree.data]);

  useEffect(() => {
    setPreview(null);
    setAck(false);
    setError(null);
  }, [dest, folderId]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      setPreview(await post<Preview>(`/api/notes/${props.noteId}/move/preview`, { workspaceId: dest, folderId: folderId || null }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const doMove = async () => {
    setLoading(true);
    setError(null);
    try {
      await post(`/api/notes/${props.noteId}/move`, { workspaceId: dest, folderId: folderId || null, relatedAction: choice, acknowledgeVisibility: ack });
      if (choice === "cancel") {
        props.onOpenChange(false);
        return;
      }
      toast.success(`Moved “${props.noteTitle}”. Its links still work.`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.tree(props.currentWorkspaceId) }),
        qc.invalidateQueries({ queryKey: qk.tree(dest) }),
        qc.invalidateQueries({ queryKey: qk.note(props.noteId) }),
        qc.invalidateQueries({ queryKey: ["tasks"] }),
      ]);
      useUi.getState().updateTab(`note:${props.noteId}`, { workspaceId: dest });
      props.onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError && e.details?.blockers) setError(`${e.message}`);
      else setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const crossWorkspace = preview && !preview.sameWorkspace;
  const movableCount = preview?.related.filter((r) => r.action !== "warning").length ?? 0;
  const canMove =
    preview &&
    preview.blockers.length === 0 &&
    (!preview.visibility.requiresAcknowledgement || ack || choice === "cancel");

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Move “{props.noteTitle}”</DialogTitle>
          <DialogDescription>Links, tasks and evidence that point to this note keep working after the move, for people who can open it.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="move-ws">Move to (notebook or project)</Label>
            <select id="move-ws" className="w-full rounded-md border bg-background px-2 py-1.5 text-sm" value={dest} onChange={(e) => { setDest(e.target.value); setFolderId(""); }}>
              {workspaces.data?.filter((w) => !w.archivedAt || w.id === props.currentWorkspaceId).map((w) => (
                <option key={w.id} value={w.id}>
                  {`${w.name} (${w.kind === "notebook" ? "notebook" : "project"}${w.memberCount > 1 ? `, ${w.memberCount} members` : ", private"})`}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="move-folder">Folder</Label>
            <select id="move-folder" className="w-full rounded-md border bg-background px-2 py-1.5 text-sm" value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">Top level (no folder)</option>
              {folderOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!preview && (
          <Button variant="outline" onClick={loadPreview} disabled={loading}>
            {loading ? "Checking…" : "Check what this move affects"}
          </Button>
        )}
        {preview && (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/50 p-3 text-sm">
              <p className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  <strong>Visibility:</strong> {preview.visibility.explanation}
                </span>
              </p>
            </div>
            {crossWorkspace && preview.related.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-medium">Related records</h3>
                <ul className="divide-y rounded-md border text-sm">
                  {preview.related.map((r, i) => (
                    <li key={`${r.type}-${r.id}-${i}`} className="flex gap-2 p-2">
                      <span className="mt-0.5 shrink-0" aria-hidden>
                        {r.action === "blocker" ? <Ban className="size-4 text-destructive" /> : r.action === "copy" ? <Copy className="size-4" /> : r.action === "warning" || r.action === "clear" ? <TriangleAlert className="size-4 text-warning" /> : <MoveRight className="size-4" />}
                      </span>
                      <span className="min-w-0">
                        <span className="font-medium">{r.label}</span> — <span className="text-muted-foreground">{ACTION_LABEL[r.action]}.</span> {r.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.blockers.length > 0 && (
              <p className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm" role="alert">
                This note cannot move yet: {preview.blockers.length} related record(s) cannot follow safely. Resolve them (for example remove the link) or keep the note where it is.
              </p>
            )}
            {crossWorkspace && preview.blockers.length === 0 && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Choose</legend>
                <label className="flex items-start gap-2 text-sm">
                  <input type="radio" name="move-choice" checked={choice === "move_related"} onChange={() => setChoice("move_related")} className="mt-1" />
                  <span>Move the note{movableCount ? ` and its ${movableCount} eligible related record(s) as listed` : ""}</span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input type="radio" name="move-choice" checked={choice === "cancel"} onChange={() => setChoice("cancel")} className="mt-1" />
                  <span>Keep the note where it is</span>
                </label>
              </fieldset>
            )}
            {preview.visibility.requiresAcknowledgement && choice !== "cancel" && preview.blockers.length === 0 && (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} className="mt-0.5" />
                <span>I understand who will be able to see this note (and the related records) after the move.</span>
              </label>
            )}
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          {preview && (
            <Button onClick={doMove} disabled={!canMove || loading}>
              {choice === "cancel" ? "Keep in place" : loading ? "Moving…" : "Move"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
